import { QuestionnaireItemComponent, QuestionnaireItemOption } from '.';
import { IBackboneElement } from '../BackboneElement';
import { isCodeableConceptEqual } from '../CodeableConcept';
import {
  IExtensionValueBoolean,
  IExtensionValueCodeableConcept,
  IExtensionValueUri,
} from '../Extension';
import {
  getExtension,
  getFirstExtension,
  getModifierExtension,
} from '../Extension/helpers';
import {
  isExtensionBoolean,
  isExtensionCodeableConcept,
  isExtensionInteger,
} from '../Extension/typeGuards';

type NoUndefinedFeild<T> = {
  [P in keyof T]-?: NoUndefinedFeild<NonNullable<T[P]>>;
};

export const getPromptTexts = (item: QuestionnaireItemComponent) => {
  const exts = getExtension(item, itemControlExtensionUrl);
  return exts
    .filter(
      (x): x is NoUndefinedFeild<IExtensionValueCodeableConcept> =>
        isExtensionCodeableConcept(x) &&
        isCodeableConceptEqual(
          x.valueCodeableConcept,
          createInformationExtension('').valueCodeableConcept,
        ),
    )
    .map(x => x.valueCodeableConcept.text);
};

export const isExclusive = (item: QuestionnaireItemOption) => {
  const exts = getModifierExtension(item, questionnaireOptionExclusiveUrl);
  return exts.some(e => isExtensionBoolean(e) && e.valueBoolean);
};

export const getSupportingLinkUris = (item: QuestionnaireItemComponent) => {
  const exts = getExtension(item, supportingLinkUrl) as IExtensionValueUri[];
  return exts.map(x => x.valueUri);
};

export const getSlider = (item: QuestionnaireItemComponent) => {
  const exts = getExtension(item, itemControlExtensionUrl);
  return exts
    .filter(
      (x): x is NoUndefinedFeild<IExtensionValueCodeableConcept> =>
        isExtensionCodeableConcept(x) &&
        isCodeableConceptEqual(
          x.valueCodeableConcept,
          createSliderExtension().valueCodeableConcept,
        ),
    )
    .map(x => x.valueCodeableConcept.text);
};

export const isSlider = (item: QuestionnaireItemComponent) =>
  getSlider(item).length > 0;

export const getTable = (item: IBackboneElement) => {
  const tableExt = createTableExtension();
  const exts = getExtension(item, itemControlExtensionUrl);
  return exts
    .filter(
      (x): x is NoUndefinedFeild<IExtensionValueCodeableConcept> =>
        isExtensionCodeableConcept(x) &&
        isCodeableConceptEqual(
          x.valueCodeableConcept,
          tableExt.valueCodeableConcept,
        ),
    )
    .map(x => x.valueCodeableConcept.text);
};

export const isTable = (item: IBackboneElement) => getTable(item).length > 0;

export const getHidden = (item: QuestionnaireItemComponent) => {
  return getExtension(item, questionnaireHiddenUrl);
};

export const isHidden = (item: QuestionnaireItemComponent) =>
  getHidden(item).length > 0;

export const minValueUrl = 'http://hl7.org/fhir/StructureDefinition/minValue';

export const getMinValue = (item: QuestionnaireItemComponent) => {
  const ext = getFirstExtension(item, minValueUrl);
  return ext && isExtensionInteger(ext) ? ext.valueInteger : undefined;
};

export const maxValueUrl = 'http://hl7.org/fhir/StructureDefinition/maxValue';

export const getMaxValue = (item: QuestionnaireItemComponent) => {
  const ext = getFirstExtension(item, maxValueUrl);
  return ext && isExtensionInteger(ext) ? ext.valueInteger : undefined;
};

export const itemControlExtensionUrl =
  'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl';

export const createInformationExtension = (
  text: string,
): IExtensionValueCodeableConcept => ({
  url: itemControlExtensionUrl,
  valueCodeableConcept: {
    text,
    coding: [
      {
        system: 'http://hl7.org/fhir/questionnaire-item-control',
        code: 'prompt',
      },
    ],
  },
});

export const createSliderExtension = (): IExtensionValueCodeableConcept => ({
  url: itemControlExtensionUrl,
  valueCodeableConcept: {
    coding: [
      {
        system: 'http://hl7.org/fhir/questionnaire-item-control',
        code: 'slider',
      },
    ],
  },
});

export const createTableExtension = (): IExtensionValueCodeableConcept => ({
  url: itemControlExtensionUrl,
  valueCodeableConcept: {
    coding: [
      {
        system: 'http://hl7.org/fhir/questionnaire-item-control',
        code: 'table',
      },
    ],
  },
});

export const supportingLinkUrl =
  'http://hl7.org/fhir/StructureDefinition/questionnaire-supportLink';
export const createSupportingLinkExtension = (
  uri: string,
): IExtensionValueUri => ({
  url: supportingLinkUrl,
  valueUri: uri,
});

export const questionnaireHiddenUrl =
  'http://hl7.org/fhir/StructureDefinition/questionnaire-hidden';
export const createHiddenExtension = (
  hide: boolean,
): IExtensionValueBoolean => ({
  url: questionnaireHiddenUrl,
  valueBoolean: hide,
});

export const questionnaireOptionExclusiveUrl =
  'http://hl7.org/fhir/StructureDefinition/questionnaire-optionExclusive';
export const createExclusiveExtension = (): IExtensionValueBoolean => ({
  url: questionnaireOptionExclusiveUrl,
  valueBoolean: true,
});
